import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

type ToastItem = { id: number; text: string; undo?: () => void };
type ToastApi = { show(text: string, undo?: () => void): void };

const Ctx = createContext<ToastApi>({ show: () => undefined });

/** トースト（画面下の中央に 2.4 秒、「元に戻す」付きは 8 秒） */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const remove = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (text: string, undo?: () => void) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-2), { id, text, undo }]);
      window.setTimeout(() => remove(id), undo ? 8000 : 2400);
    },
    [remove],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="toast" data-testid="toast">
            <span>{t.text}</span>
            {t.undo && (
              <button
                type="button"
                onClick={() => {
                  t.undo?.();
                  remove(t.id);
                }}
              >
                元に戻す
              </button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(Ctx);
}
